/**
 * KEIKKA i18n - Main Entry Point v8.0
 * 
 * ALL locale-related imports should come from here.
 * This file re-exports everything from localeManager.js and provides translations.
 * 
 * SUPPORTED LANGUAGES (7):
 * - Estonian (et) - Default
 * - English (en) - Fallback
 * - Finnish (fi)
 * - Swedish (sv)
 * - Norwegian (nb)
 * - Russian (ru)
 * - Spanish (es) - NEW
 */

// =============================================================================
// RE-EXPORT EVERYTHING FROM LOCALE MANAGER (Single Source of Truth)
// =============================================================================
export {
  // Core functions
  getLocale,
  setLocale,
  detectLocale,
  initializeLocaleManager,
  subscribeToLocaleChanges,
  cleanupLocaleManager,
  
  // Aliases for compatibility
  getLanguage,
  setLanguage,
  detectLanguage,
  initializeI18n,
  onLanguageChange,
  cleanupI18n,
  
  // Utilities
  normalizeLocale,
  normalizeLanguage,
  isLocaleSupported,
  isTierA,
  isPrimaryLocale,
  isRTL,
  getTextDirection,
  getLocaleInfo,
  getLanguageInfo,
  searchLanguages,
  getLocaleFromUrl,
  setLocaleInUrl,
  getLanguageFromUrl,
  setLanguageInUrl,
  
  // Debug
  getDebugInfo,
  
  // Constants
  SUPPORTED_LOCALES,
  ALL_LANGUAGES,
  TIER_A_LANGUAGES,
  RTL_LANGUAGES,
  DEFAULT_LANGUAGE,
  FALLBACK_LANGUAGE,
  DEFAULT_LOCALE,
  FALLBACK_LOCALE,
  LANGUAGE_MAP,
  STORAGE_KEY,
  COOKIE_NAME,
  LOCALE_CONFIG,
  LOCALE_INFO,
} from './localeManager';

import localeManager from './localeManager';
export { localeManager as LangManager };
export default localeManager;

// =============================================================================
// TRANSLATION FILES (All supported languages)
// =============================================================================
import en from './locales/en.json';
import et from './locales/et.json';
import fi from './locales/fi.json';
import sv from './locales/sv.json';
import nb from './locales/nb.json';
import ru from './locales/ru.json';
import es from './locales/es.json';

export const translations = {
  en,
  et,
  fi,
  sv,
  nb,
  ru,
  es,
};

// =============================================================================
// TRANSLATOR FUNCTION
// =============================================================================

/**
 * Get nested value from object by dot-notation path
 */
function getNestedValue(obj, path) {
  if (!obj || !path) return null;
  
  const keys = path.split('.');
  let value = obj;
  
  for (const key of keys) {
    if (value && typeof value === 'object' && key in value) {
      value = value[key];
    } else {
      return null;
    }
  }
  
  return typeof value === 'string' ? value : null;
}

/**
 * Fallback when a translation key is missing across every source language.
 *
 * Policy (strict): we NEVER show a humanized key ("Intent Find Work") to users.
 * In production we return an empty string so the element collapses visually
 * rather than leaking key structure. In development we return a tagged marker
 * so missing keys are impossible to miss during QA/testing.
 */
function keyToReadable(key) {
  if (!key) return '';
  if (process.env.NODE_ENV === 'development') {
    return `[MISSING:${key}]`;
  }
  return '';
}

// Track missing keys (dev only)
const missingKeys = new Set();

/**
 * Create a translator function for a specific language
 * 
 * Fallback chain:
 * 1. Current language
 * 2. English (fallback)
 * 3. Estonian (master)
 * 4. Key-to-readable conversion (last resort)
 * 
 * @param {string} lang - Language code (e.g., 'et', 'en', 'es')
 * @returns {Function} - Translation function t(key, params)
 */
export function createTranslator(lang) {
  const normalizedLang = localeManager.normalizeLocale(lang) || localeManager.DEFAULT_LANGUAGE;
  
  // Get translation sources
  const currentTrans = translations[normalizedLang] || {};
  const fallbackTrans = translations[localeManager.FALLBACK_LANGUAGE] || translations.en;
  const masterTrans = translations.et; // Estonian is the master
  
  return (key, params = {}) => {
    if (!key) return '';
    
    // Try current language first
    let value = getNestedValue(currentTrans, key);
    
    // Try English fallback
    if (!value && normalizedLang !== 'en') {
      value = getNestedValue(fallbackTrans, key);
    }
    
    // Try Estonian master
    if (!value && normalizedLang !== 'et') {
      value = getNestedValue(masterTrans, key);
    }
    
    // Log missing key (dev + nb production — we need a Norway-ready build)
    if (!value) {
      const keyId = `${normalizedLang}:${key}`;
      if (!missingKeys.has(keyId)) {
        missingKeys.add(keyId);
        if (
          process.env.NODE_ENV === 'development' ||
          normalizedLang === 'nb' ||
          normalizedLang === 'no'
        ) {
          // Also push to a global array so automated QA can read it.
          if (typeof window !== 'undefined') {
            window.__keikka_i18n_missing = window.__keikka_i18n_missing || [];
            window.__keikka_i18n_missing.push({ locale: normalizedLang, key });
          }
          // eslint-disable-next-line no-console
          console.warn(`[i18n-missing] locale=${normalizedLang} key=${key}`);
        }
      }
    }
    
    // Final fallback: convert key to readable text
    if (!value) {
      return keyToReadable(key);
    }
    
    // Interpolation: replace {{param}} or {param} with values
    if (Object.keys(params).length > 0) {
      return value.replace(/\{\{?(\w+)\}?\}/g, (match, paramKey) => {
        return params[paramKey] !== undefined ? String(params[paramKey]) : match;
      });
    }
    
    return value;
  };
}

/**
 * Get all missing translation keys (for debugging)
 */
export function getMissingKeys() {
  return Array.from(missingKeys);
}

/**
 * Clear missing keys tracker
 */
export function clearMissingKeys() {
  missingKeys.clear();
}

/**
 * Check if current language is auto-translated
 * Returns false - all supported languages have full translations
 */
export function isAutoTranslated(lang) {
  return false;
}

/**
 * Get supported locale codes
 */
export function getSupportedLocaleCodes() {
  return localeManager.TIER_A_LANGUAGES;
}
